import type { Command } from '../types/command.types';

/**
 * Built-in commands for panel navigation and control
 */
export const panelCommands: Command[] = [
  {
    id: 'panel.focus.left',
    label: 'Focus Left Panel',
    description: 'Set focus to the left panel',
    icon: '◀',
    category: 'Panel Navigation',
    shortcut: 'Alt+1',
    priority: 100,
    keywords: ['left', 'sidebar', 'focus', 'switch'],
    execute: (context) => {
      context.setFocus('left');
    },
    isAvailable: (context) => context.focusedPanel !== 'left',
  },
  {
    id: 'panel.focus.middle',
    label: 'Focus Middle Panel',
    description: 'Set focus to the middle panel',
    icon: '▣',
    category: 'Panel Navigation',
    shortcut: 'Alt+2',
    priority: 100,
    keywords: ['middle', 'center', 'main', 'focus', 'switch'],
    execute: (context) => {
      context.setFocus('middle');
    },
    isAvailable: (context) => context.focusedPanel !== 'middle',
  },
  {
    id: 'panel.focus.right',
    label: 'Focus Right Panel',
    description: 'Set focus to the right panel',
    icon: '▶',
    category: 'Panel Navigation',
    shortcut: 'Alt+3',
    priority: 100,
    keywords: ['right', 'sidebar', 'focus', 'switch'],
    execute: (context) => {
      context.setFocus('right');
    },
    isAvailable: (context) => context.focusedPanel !== 'right',
  },
  {
    id: 'panel.toggle.left',
    label: 'Toggle Left Panel',
    description: 'Expand or collapse the left panel',
    icon: '◀◀',
    category: 'Panel Control',
    keywords: ['toggle', 'left', 'collapse', 'expand', 'hide', 'show'],
    priority: 80,
    execute: (context) => {
      context.events?.emit({
        type: 'panel:toggle',
        source: 'command-palette',
        timestamp: Date.now(),
        payload: { panelId: 'left' },
      });
    },
  },
  {
    id: 'panel.toggle.right',
    label: 'Toggle Right Panel',
    description: 'Expand or collapse the right panel',
    icon: '▶▶',
    category: 'Panel Control',
    keywords: ['toggle', 'right', 'collapse', 'expand', 'hide', 'show'],
    priority: 80,
    execute: (context) => {
      context.events?.emit({
        type: 'panel:toggle',
        source: 'command-palette',
        timestamp: Date.now(),
        payload: { panelId: 'right' },
      });
    },
  },
  {
    id: 'panel.collapse.all',
    label: 'Collapse All Panels',
    description: 'Collapse all side panels to maximize middle panel',
    icon: '⬌',
    category: 'Panel Control',
    keywords: ['collapse', 'hide', 'all', 'maximize', 'focus mode'],
    priority: 70,
    execute: (context) => {
      context.events?.emit({
        type: 'panel:collapse-all',
        source: 'command-palette',
        timestamp: Date.now(),
        payload: {},
      });
    },
  },
  {
    id: 'panel.expand.all',
    label: 'Expand All Panels',
    description: 'Expand all side panels to default layout',
    icon: '⬍',
    category: 'Panel Control',
    keywords: ['expand', 'show', 'all', 'restore', 'default'],
    priority: 70,
    execute: (context) => {
      context.events?.emit({
        type: 'panel:expand-all',
        source: 'command-palette',
        timestamp: Date.now(),
        payload: {},
      });
    },
  },
  {
    id: 'panel.refresh',
    label: 'Refresh Panel Data',
    description: 'Reload data for the currently focused panel',
    icon: '↻',
    category: 'Panel Control',
    keywords: ['refresh', 'reload', 'update', 'sync'],
    priority: 60,
    execute: (context) => {
      const panelId = context.focusedPanel;
      if (!panelId) return;

      context.events?.emit({
        type: 'panel:refresh',
        source: 'command-palette',
        timestamp: Date.now(),
        payload: { panelId },
      });
    },
    isAvailable: (context) => context.focusedPanel !== null,
  },
  {
    id: 'panel.reset.layout',
    label: 'Reset Panel Layout',
    description: 'Reset panel sizes to default layout',
    icon: '⟲',
    category: 'Panel Control',
    keywords: ['reset', 'layout', 'default', 'restore'],
    priority: 50,
    execute: (context) => {
      context.events?.emit({
        type: 'panel:reset-layout',
        source: 'command-palette',
        timestamp: Date.now(),
        payload: {},
      });
    },
  },
];

/**
 * Get panel commands
 * @returns Array of panel-related commands
 */
export function getPanelCommands(): Command[] {
  return panelCommands;
}
